// Wealth Identity Assessment — questions, micro-validations, supporting copy

export const PATTERNS = {
  upgrader: { key: "upgrader", name: "The Upgrader", section: 1, summary: "Your income has grown. Your wealth hasn't followed." },
  exposed: { key: "exposed", name: "The Exposed", section: 2, summary: "Everything looks stable. The foundation isn't built yet." },
  performer: { key: "performer", name: "The Performer", section: 3, summary: "The appearance of success is quietly costing actual success." },
  uninformed: { key: "uninformed", name: "The Uninformed", section: 4, summary: "Good decisions are impossible without good information. You haven't had access to the right information yet." },
};

export const SECTIONS = [
  {
    index: 1,
    label: "Section One of Four",
    heading: "How your money moves\nwith your life.",
    body: "The next four questions are about what happens to your income as it grows — and what your financial future actually looks like in your mind right now.\n\nAnswer from instinct. The first answer that comes to you is usually the honest one.",
    pattern: "upgrader",
  },
  {
    index: 2,
    label: "Section Two of Four",
    heading: "What holds when\nsomething breaks.",
    body: "The next four questions are about your financial safety net — what's actually there if something goes wrong.\n\nThis section asks you to imagine scenarios most people avoid thinking about. That discomfort is useful information.",
    pattern: "exposed",
  },
  {
    index: 3,
    label: "Section Three of Four",
    heading: "What your money\nis communicating.",
    body: "The next four questions are about the relationship between how your financial life looks and what it actually is.\n\nThis is the most honest section of the assessment. It requires looking at something most people don't look at directly.",
    pattern: "performer",
  },
  {
    index: 4,
    label: "Section Four of Four",
    heading: "What you know —\nand what you don't.",
    body: "The final four questions are about your relationship with financial knowledge — how well you understand the systems your money moves through.\n\nThere is no shame in any answer here. The gap between what you know and what's available to know is almost never your fault.",
    pattern: "uninformed",
  },
];

// 16 questions. Score: A=4, B=3, C=2, D=1.
// Each option is [text, score, microValidation]
export const QUESTIONS = [
  // ---- Section 1: Lifestyle / Upgrader ----
  {
    section: 1,
    prompt: "It's Friday afternoon. Your direct deposit hits and it's $2,000 more than last month — a bonus, a strong commission, a raise finally coming through. By the following Friday, that extra $2,000 has most likely:",
    options: [
      ["Moved into savings or an investment account. That was always the plan.", 4, "This is rarer than most people admit. Good."],
      ["Been split — some intentionally saved, some spent on something I wanted.", 3, "Awareness plus some action. That's honest."],
      ["Quietly disappeared into the month. Better food, a few upgrades, nothing I can fully account for.", 2, "This is the most common answer among high earners. You're not alone."],
      ["I genuinely couldn't tell you. It's just... gone.", 1, "More people than you'd think selected this. That honesty matters."],
    ],
  },
  {
    section: 1,
    prompt: "Someone asks you right now: \"How much do you spend every month on subscriptions and recurring charges?\" Your honest answer is:",
    options: [
      ["I know the exact number. I reviewed it recently and cut what I wasn't using.", 4, "Most people have never done this. You have."],
      ["I have a rough sense — probably within $50 of the real number.", 3, "Awareness without full clarity. That's a real and honest place to be."],
      ["I'd have to check. There are things on there I've forgotten about.", 2, "Recurring expenses are the quietest kind. Very common."],
      ["I'd rather not look. I know it's more than it should be.", 1, "The avoidance itself is information. Worth knowing."],
    ],
  },
  {
    section: 1,
    prompt: "You sit down to think about where you'll be financially in five years. What actually comes into focus?",
    options: [
      ["Specific assets. A number. A structure I'm already building toward.", 4, "A specific vision is rare. This is a real asset."],
      ["A better life — more income, more freedom, more of what I enjoy now.", 3, "A better version of today is a valid answer — and a common one."],
      ["Something like today, hopefully with less financial stress.", 2, "Vague futures are more common than people admit."],
      ["Honestly, I change the subject in my head. It makes me uneasy.", 1, "Avoidance of the future question is itself a data point. Important one."],
    ],
  },
  {
    section: 1,
    prompt: "I ask you: \"What percentage of everything you earned last year did you actually keep?\" Your response is:",
    options: [
      ["I know the number. I track it and it's intentional.", 4, "Tracking savings rate is a habit most high earners haven't built. Noted."],
      ["I have a rough idea. Not exact, but I'm aware of it.", 3, "Approximate awareness is a starting point."],
      ["I'd have to calculate it. I've never really thought about it that way.", 2, "Most people have never run this calculation. You're in the majority."],
      ["I have no idea. I focus on what comes in, not what stays.", 1, "Income focus without retention awareness is one of the most common patterns."],
    ],
  },
  // ---- Section 2: Security / Exposed ----
  {
    section: 2,
    prompt: "It's Monday morning and your income stops. No warning. Job loss, health issue, business dries up — doesn't matter. You need to maintain your life as it is right now. How long before you're forced into debt?",
    options: [
      ["Six months, minimum. I have a dedicated fund for exactly this.", 4, "Six months of runway changes how every other decision gets made."],
      ["Three to six months. It wouldn't be comfortable, but I'd manage.", 3, "Functional but fragile is an honest place. Most people are here."],
      ["Maybe a month or two. Then things would get very stressful, very fast.", 2, "One to two months is more common than the financial advice world admits."],
      ["Weeks. I'd be making hard calls before the month was out.", 1, "This answer takes courage to select. It's the right one if it's true."],
    ],
  },
  {
    section: 2,
    prompt: "Your car needs $3,000 in repairs. You find out on a Wednesday. By Friday, you've:",
    options: [
      ["Paid it from a dedicated emergency fund. This is what it's there for.", 4, "A dedicated fund that works as intended. That's the goal."],
      ["Figured it out — pulled from savings, shifted some things. Stressful but handled.", 3, "Managed but stressful is real. And honest."],
      ["Put it on a card. I'll deal with it next month and the month after.", 2, "Credit card handling of emergencies is the most statistically common response."],
      ["Entered a stress spiral. This kind of thing derails me every time.", 1, "If this is true, it's important to know. That's why this question exists."],
    ],
  },
  {
    section: 2,
    prompt: "You're unable to work for six months. Injury, illness — something real. The financial protection you have in place right now would:",
    options: [
      ["Cover me. I've thought this through and made sure I'm protected.", 4, "Intentional protection coverage is uncommon. Worth noting."],
      ["Partially cover me. There are gaps I know exist but haven't closed.", 3, "Partial coverage awareness is better than none."],
      ["Give me whatever came with my job. I've never actually looked at whether it's enough.", 2, "Defaulting to employer coverage without evaluation is extremely common."],
      ["Do nothing. I don't have real protection in place and I know it.", 1, "Knowing the gap exists is the first step to closing it."],
    ],
  },
  {
    section: 2,
    prompt: "Three bad things happen in the same month — a medical bill, a slow income month, and a home repair. You're sitting with all three at once. Your financial foundation right now means:",
    options: [
      ["I handle it. Uncomfortable but nothing breaks.", 4, "A foundation that holds under pressure. That's what security actually means."],
      ["I get through it, but just barely. One more thing and I'd be in trouble.", 3, "Functional but fragile — this is where most people land."],
      ["I'm already stressed before this scenario ends. Two of those would undo me.", 2, "One thing at a time is manageable. All three at once reveals the foundation."],
      ["One of those would undo me. All three is a crisis I'm not equipped for.", 1, "Selecting this honestly is more valuable than selecting something better that isn't true."],
    ],
  },
  // ---- Section 3: Identity / Performer ----
  {
    section: 3,
    prompt: "Think about the last significant thing you bought — something over $500. Be honest about what was actually happening when you made that decision. The real driver was:",
    options: [
      ["Pure function or intentional investment. I needed it or it builds something.", 4, "Purely functional purchasing is intentional. That clarity has value."],
      ["Mostly practical, with some element of wanting it. Honest but not purely strategic.", 3, "Mixed motivation is human and honest."],
      ["A mix of want and image. How it would look was part of the calculation.", 2, "Image as a purchase driver is more common than anyone discusses."],
      ["More about signal than substance. I wanted what it communicated about me.", 1, "Signal-driven purchasing is one of the most expensive invisible patterns. Naming it matters."],
    ],
  },
  {
    section: 3,
    prompt: "Someone in your circle — a friend, a colleague, someone you respect — just bought a house in a better neighborhood, took a trip you'd love to take, or upgraded something visible. In the 48 hours after you find out, you:",
    options: [
      ["Feel nothing financially. My direction is my direction.", 4, "Genuine immunity to comparison is rare. If this is true, it's worth protecting."],
      ["Notice a flicker of comparison, then let it go. I'm aware of it.", 3, "Noticing the flicker and releasing it — that's the work."],
      ["Find myself reconsidering things. It shifts something in me whether I want it to or not.", 2, "Involuntary comparison is human biology. Not a character flaw."],
      ["Start planning how I can match it or explain why I haven't yet.", 1, "External reference points running internal decisions. Very common. Very costly."],
    ],
  },
  {
    section: 3,
    prompt: "Your income drops 30% starting next month. It's not temporary — you need to restructure your life around this new number. The thing that would be hardest to let go of isn't a necessity. It's:",
    options: [
      ["Nothing comes to mind. I live intentionally below what I earn.", 4, "Intentional living below income is one of the quietest wealth-building habits."],
      ["A few things I enjoy, but nothing that would shake my sense of stability.", 3, "Adjustment without identity disruption. That's resilience."],
      ["Something that would feel like a visible step backward. People would notice.", 2, "Visible step backward as a concern — this is where identity and finances intersect."],
      ["Most of how my life looks right now. The life and the income are deeply tied.", 1, "Life and income deeply tied is a signal, not a judgment."],
    ],
  },
  {
    section: 3,
    prompt: "If someone who knew your exact net worth, savings rate, and balance sheet looked at how your life appears from the outside — your home, your car, your clothes, your trips — their reaction would be:",
    options: [
      ["\"Makes sense. The life matches the financial position.\"", 4, "Alignment between appearance and reality is rare and valuable."],
      ["\"Mostly accurate. Maybe slightly aspirational in a few areas.\"", 3, "Slightly aspirational in places — honest and common."],
      ["\"There's a gap here. The outside looks ahead of where the numbers are.\"", 2, "A gap between the image and the balance sheet is the most costly invisible debt."],
      ["\"The image and the balance sheet are telling completely different stories.\"", 1, "Naming this gap is the beginning of closing it."],
    ],
  },
  // ---- Section 4: Knowledge / Uninformed ----
  {
    section: 4,
    prompt: "Someone asks you to explain — in plain language, not financial jargon — how your money is actually growing right now. Not your income. Your money. You:",
    options: [
      ["Walk them through it clearly. I know exactly what my money is doing and why.", 4, "This level of clarity about money mechanics is uncommon. Real asset."],
      ["Give a decent explanation with a few gaps. I understand most of it.", 3, "Working understanding with gaps — honest and fixable."],
      ["Give the surface-level answer. I know the concepts but not how they apply to me specifically.", 2, "Concepts without application is where most financial education leaves people."],
      ["Deflect or generalize. I don't actually know, and the question makes me uncomfortable.", 1, "The discomfort is information. It points exactly to the gap."],
    ],
  },
  {
    section: 4,
    prompt: "You're sitting with your bank account, your insurance policy, and your investment account open in front of you. Someone asks: \"How does each of these actually make or protect money for you?\" You:",
    options: [
      ["Explain each one confidently. I chose them intentionally and I understand how they work.", 4, "Intentional product selection with real understanding. Rare."],
      ["Get through most of it. There are gaps but I have a working understanding.", 3, "Working knowledge with gaps — the most common honest answer."],
      ["Struggle. I use these because someone told me to. I've never fully understood the mechanics.", 2, "Using products without understanding them is the norm, not the exception."],
      ["Close the laptop. This is the kind of question I actively avoid because it exposes something I don't want to face.", 1, "Avoidance of this question in real life is extremely common. Naming it here matters."],
    ],
  },
  {
    section: 4,
    prompt: "It's Sunday. You have two unscheduled hours. Financial education — a book, a podcast, a deep-dive into how something works — how likely is it that you spend any of that time there?",
    options: [
      ["Very likely. This is genuinely part of how I spend my time and I enjoy it.", 4, "Active financial curiosity is a genuine wealth-building behavior."],
      ["Somewhat likely, if something relevant comes up. I engage when prompted.", 3, "Reactive engagement is still engagement. A starting point."],
      ["Unlikely. I wouldn't know where to start, and I don't fully trust what's out there.", 2, "Not knowing where to start — or not trusting the sources — is valid and common."],
      ["It won't happen. The topic creates more anxiety than curiosity for me.", 1, "Anxiety around this topic is more universal than the financial world acknowledges."],
    ],
  },
  {
    section: 4,
    prompt: "You're at dinner with someone you respect — financially sharp, the kind of person whose opinion matters to you. They ask: \"So what's your money actually doing for you right now — beyond the paycheck?\" The feeling in your chest in that moment is:",
    options: [
      ["Calm. I know exactly what my money is doing and I'm comfortable saying it.", 4, "This kind of financial confidence comes from real knowledge. It's earned."],
      ["Mostly fine. I have a picture, even if it's not complete.", 3, "Mostly fine with an incomplete picture — honest and common."],
      ["A quiet panic I'm hiding behind a confident answer.", 2, "Quiet panic behind a confident exterior. You are not alone in this."],
      ["Pure exposure. I don't know, and the question just made that very clear.", 1, "Pure exposure. The most honest answer on this list. Worth knowing."],
    ],
  },
];

// Score label thresholds
export function scoreLabel(score) {
  if (score >= 13) return "Strong";
  if (score >= 9) return "Developing";
  if (score >= 5) return "Vulnerable";
  return "Critical";
}

// Compute section scores from 16 answers (array of numeric 1-4, in order)
export function computeScores(answers) {
  const s1 = answers.slice(0, 4).reduce((a, b) => a + b, 0);
  const s2 = answers.slice(4, 8).reduce((a, b) => a + b, 0);
  const s3 = answers.slice(8, 12).reduce((a, b) => a + b, 0);
  const s4 = answers.slice(12, 16).reduce((a, b) => a + b, 0);
  return { s1, s2, s3, s4 };
}

// Dominant section = LOWEST score. Tiebreaker = most 1-point answers in that section's questions.
export function computeDominant(answers) {
  const { s1, s2, s3, s4 } = computeScores(answers);
  const sections = [
    { i: 1, score: s1, pattern: "upgrader", range: [0, 4] },
    { i: 2, score: s2, pattern: "exposed", range: [4, 8] },
    { i: 3, score: s3, pattern: "performer", range: [8, 12] },
    { i: 4, score: s4, pattern: "uninformed", range: [12, 16] },
  ];
  const minScore = Math.min(s1, s2, s3, s4);
  const tied = sections.filter((s) => s.score === minScore);
  if (tied.length === 1) return tied[0].pattern;
  // Tiebreaker: most 1-point answers in the section
  let best = tied[0];
  let bestOnes = -1;
  for (const t of tied) {
    const ones = answers.slice(t.range[0], t.range[1]).filter((a) => a === 1).length;
    if (ones > bestOnes) { best = t; bestOnes = ones; }
  }
  return best.pattern;
}

// Share text per dominant pattern
export const SHARE_TEXT = {
  upgrader: "Just took the Wealth Identity Assessment and found out I'm The Upgrader. The description was uncomfortably accurate. If you make decent money and still feel like you should be further ahead — this is worth 4 minutes: lizdirocco.com/quiz",
  exposed: "Just took the Wealth Identity Assessment — I'm The Exposed. The part about financial security being different from financial income actually made me stop. Worth 4 minutes: lizdirocco.com/quiz",
  performer: "Just took this financial assessment and got The Performer. I'm still thinking about the 'what this pattern says in silence' section. Take it: lizdirocco.com/quiz",
  uninformed: "Just found out I'm The Uninformed on the Wealth Identity Assessment. The part about the financial industry's conflict of interest hit differently. 4 minutes: lizdirocco.com/quiz",
};

// Supporting pattern commentary (when this is NOT the dominant pattern)
export const SUPPORTING_COPY = {
  upgrader: {
    Strong: "Your spending habits are intentional and your savings rate reflects it. The opportunity is ensuring that intentionality is backed by the right financial structure — not just discipline.",
    Developing: "You're aware of how lifestyle and income interact and managing it — mostly. The gap between managing and optimizing is worth closing. Small redirections compound meaningfully.",
    Vulnerable: "Lifestyle has likely been tracking income closely — which means wealth-building has been quiet. This is one of the most common and most fixable patterns among high earners.",
    Critical: "Income growth has not translated to wealth growth. This isn't a judgment — it's a starting point. The pattern is clear, and clear patterns can be changed.",
  },
  exposed: {
    Strong: "Your financial safety net is real and you've thought about it. That foundation allows every other financial decision to be made from strength rather than fear.",
    Developing: "You have some protection in place, but gaps exist. Closing them before you need them is the priority. Financial resilience isn't built in a crisis.",
    Vulnerable: "Your financial stability depends heavily on things continuing to go right. That's a fragile position, even when it doesn't feel that way.",
    Critical: "You're operating without a meaningful safety net. One disruption changes everything. This is the foundational work everything else depends on.",
  },
  performer: {
    Strong: "External pressure has limited influence on your financial decisions — you're clear on your own direction. That clarity is rare. Keep protecting it.",
    Developing: "You notice when comparison or image influences spending and you course-correct. The work is making that correction more automatic.",
    Vulnerable: "Financial choices are carrying more social weight than you'd consciously choose. The gap between the image and the balance sheet is where wealth quietly disappears.",
    Critical: "The performance of success is significantly outpacing the building of it. This pattern is psychologically complex and financially consequential. The shift is available.",
  },
  uninformed: {
    Strong: "Financial curiosity is clearly part of how you operate. The opportunity is deepening that knowledge into active strategy — not just awareness.",
    Developing: "You understand the concepts but feel the gap between knowing and applying. That gap is normal and closes faster than most people expect.",
    Vulnerable: "Real gaps exist in how financial systems and products work — and those gaps are influencing decisions you're making right now, without realizing it.",
    Critical: "Financial decision-making has been happening without a full picture of how money actually works. That's an information gap. Information gaps close quickly.",
  },
};
